import React, { useState } from "react";
import { giroTechLogo, giroTechPlatformName } from "../../utils/constants";

// Determina a URL base da API
const getApiBaseUrl = () => {
  // Em desenvolvimento, usa proxy configurado no craco.config.js
  if (process.env.NODE_ENV === "development") {
    return "/api";
  }

  // Em produção, usa window.REACT_APP_BACKEND_URL (injetado pelo entrypoint.sh)
  const envUrl = window.REACT_APP_BACKEND_URL;

  // Se envUrl está vazio ou é undefined, usa URL relativa para produção
  if (!envUrl || envUrl === "") {
    return "/api";
  }

  // Se a URL já termina com /api, usa como está
  if (envUrl.endsWith("/api")) {
    return envUrl;
  }

  // Se a URL não tem /api, adiciona
  return envUrl + "/api";
};

const API_BASE_URL = getApiBaseUrl();

const RegistrationForm = ({ onSuccess, onBackToLogin }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    whatsapp: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const formatWhatsApp = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");

    // Format as: 99 999 999 999
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 5) {
      return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    } else if (digits.length <= 8) {
      return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
    } else {
      return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(
        8,
        11
      )}`;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "whatsapp") {
      const formatted = formatWhatsApp(value);
      setFormData((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!acceptedTerms) {
      setError("Você precisa aceitar os termos de uso e política de privacidade para continuar.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setShowSuccess(true);
        // Removed automatic redirect, user will click button to go to login
      } else {
        setError(data.detail || "Erro ao realizar cadastro");
      }
    } catch (error) {
      console.error("Registration error:", error);
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
      {/* Success Message */}
      {showSuccess ? (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md mx-auto">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-10 h-10 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Parabéns! 🎉</h2>
            <p className="text-gray-600 mb-8">
              Seu cadastro foi realizado com sucesso!
              <br />
              Agora você pode fazer login com seu email para começar a usar a plataforma.
            </p>
            <button
              onClick={onBackToLogin}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-medium transition-colors duration-200 w-full"
            >
              Ir para o Login
            </button>
          </div>
        </div>
      ) : (
        /* Layout Principal - 50% / 50% sem espaços laterais */
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
          {/* Lado Esquerdo - Informações (50%) */}
          <div className="flex items-center justify-center p-8">
            <div className="text-white text-center max-w-2xl w-full">
              <div className="mb-8">
                <img
                  src="https://giro-logos.s3.us-east-1.amazonaws.com/public/logo-branco.png"
                  alt="Giro.Tech"
                  className="h-16 mb-6 mx-auto"
                />
              </div>

              <h1 className="text-3xl lg:text-4xl font-bold mb-6 leading-tight">
                Treinamento de Crédito Estruturado
              </h1>

              <p className="text-lg lg:text-xl mb-6 opacity-90">
                A GIRO.TECH criou um Treinamento de Crédito gratuito para você começar do básico,
                até entender tudo sobre Securitizadoras e FIDCs.
              </p>

              <p className="text-base lg:text-lg opacity-80 mb-8">
                Neste curso aprenda na prática com vídeos curtos e linguagem simples, e aprofunde
                seus conhecimentos sobre Recebíveis, Bancarização e Securitização.
              </p>

              {/* Imagem abaixo do texto */}
              <div className="mt-8">
                <img
                  src="https://giro-logos.s3.us-east-1.amazonaws.com/public/post-cadastro.png"
                  alt="Treinamento"
                  className="w-full max-w-sm mx-auto"
                />
              </div>
            </div>
          </div>

          {/* Lado Direito - Formulário (50%) */}
          <div className="flex items-center justify-center p-8">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Criar conta</h2>
                <p className="text-gray-600">Comece sua jornada de aprendizado hoje</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome completo
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Seu nome completo"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="seu@email.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Empresa</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Nome da empresa"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp</label>
                    <input
                      type="tel"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="11 999 999 999"
                      maxLength="14"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Formato: 11 999 999 999</p>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                    {error}
                  </div>
                )}

                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                  />
                  <label htmlFor="acceptTerms" className="text-sm text-gray-700">
                    Eu aceito os{" "}
                    <a
                      href="https://giro.tech/politicas/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      termos de uso e política de privacidade
                    </a>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading || !acceptedTerms}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
                >
                  {loading ? "Cadastrando..." : "Criar conta"}
                </button>
              </form>

              <div className="mt-8 text-center">
                <button
                  onClick={onBackToLogin}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200"
                >
                  Já tem conta? Fazer login
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationForm;
